/**
 * Consent records: every decision any client made, newest first.
 *
 * The log is evidence, so it is read-only in every layer: no route changes a
 * record and the database refuses an UPDATE or DELETE on the table. Each entry
 * carries what was agreed to — the statement, the version and a hash of its
 * exact text — and who, when and from where, which is what a regulator or a
 * dispute asks for.
 */

import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ScrollText } from 'lucide-react';
import {
  Alert,
  api,
  ApiError,
  Badge,
  Card,
  DetailField,
  DetailGrid,
  EmptyState,
  ErrorState,
  formatDateTime,
  formatRelative,
  LoadingState,
  PageHeader,
  Pager,
  ScrollArea,
  Select,
  StatusChip,
} from '@connect/web-shared';

import { STATUS_TONES, fetchRequirements, legalKeys, type ConsentRecordRow } from './types';

const DECISIONS = [
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

export function RecordEvidence({ row }: { row: ConsentRecordRow }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-body">
        <span className="font-medium">Statement: </span>
        {row.statement}
      </p>
      <DetailGrid>
        <DetailField label="Document">
          {row.document_title} · {row.version_label}
        </DetailField>
        <DetailField label="Signed in as">{row.login}</DetailField>
        <DetailField label="From">
          {[row.platform, row.ip_address].filter(Boolean).join(' · ')}
        </DetailField>
        <DetailField label="When">{formatDateTime(row.created_at)}</DetailField>
        <DetailField label="Text SHA-256" className="col-span-2">
          <span className="font-mono text-2xs" title={row.content_sha256}>
            {row.content_sha256}
          </span>
        </DetailField>
        <DetailField label="Request">
          <span className="font-mono text-2xs">{row.request_id}</span>
        </DetailField>
        <DetailField label="Device" className="col-span-2">
          <span className="text-xs" title={row.user_agent ?? undefined}>
            {row.user_agent}
          </span>
        </DetailField>
      </DetailGrid>
    </div>
  );
}

export function ConsentRecordsPage() {
  const [requirement, setRequirement] = useState('');
  const [decision, setDecision] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [open, setOpen] = useState<string | null>(null);

  const requirements = useQuery({ queryKey: legalKeys.requirements, queryFn: fetchRequirements });
  const records = useQuery({
    queryKey: [...legalKeys.records, { requirement, decision, page, pageSize }],
    queryFn: () =>
      api.page<ConsentRecordRow>('/legal/consents', {
        params: {
          requirement: requirement || undefined,
          decision: decision || undefined,
          page,
          page_size: pageSize,
        },
      }),
    placeholderData: keepPreviousData,
  });

  const rows = records.data?.rows ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Consent records"
        description="Every accept, decline and withdrawal, with the exact version and where it was made."
        breadcrumbs={[{ label: 'Legal' }, { label: 'Consent records' }]}
      />

      <Alert tone="info" title="The record is append-only">
        A decision is never edited or deleted. A later decision is a new line, so the full history
        of each client's consent is always here.
      </Alert>

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-line p-3">
          <Select
            label="Requirement"
            placeholder="All requirements"
            containerClassName="w-64"
            value={requirement}
            options={(requirements.data ?? []).map((r) => ({ value: r.public_id, label: r.title }))}
            onChange={(event) => {
              setRequirement(event.target.value);
              setPage(1);
            }}
          />
          <Select
            label="Decision"
            placeholder="Any decision"
            containerClassName="w-44"
            value={decision}
            options={DECISIONS}
            onChange={(event) => {
              setDecision(event.target.value);
              setPage(1);
            }}
          />
        </div>

        {records.isLoading ? (
          <LoadingState label="Loading records" />
        ) : records.isError ? (
          <ErrorState
            message={records.error instanceof ApiError ? records.error.message : undefined}
            onRetry={() => void records.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<ScrollText className="size-5" />}
            title="No decisions yet"
            description="Records appear here as clients accept, decline or withdraw."
          />
        ) : (
          <ScrollArea className="">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-2xs uppercase tracking-wide text-faint">
                  <th className="w-8" />
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">Requirement</th>
                  <th className="px-3 py-2 font-medium">Version</th>
                  <th className="px-3 py-2 font-medium">Decision</th>
                  <th className="px-3 py-2 font-medium">Platform</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((row) => {
                  const expanded = open === row.public_id;
                  return (
                    <Fragment key={row.public_id}>
                      <tr
                        className="cursor-pointer hover:bg-surface-2"
                        onClick={() => setOpen(expanded ? null : row.public_id)}
                      >
                        <td className="pl-3 text-faint">
                          {expanded ? (
                            <ChevronDown className="size-4" />
                          ) : (
                            <ChevronRight className="size-4" />
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2" title={formatDateTime(row.created_at)}>
                          {formatRelative(row.created_at)}
                        </td>
                        <td className="px-3 py-2">
                          {row.client_public_id ? (
                            <Link
                              to={`/clients/${row.client_public_id}?tab=consents`}
                              className="hover:text-accent hover:underline"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {row.client_name}
                            </Link>
                          ) : (
                            row.client_name
                          )}
                        </td>
                        <td className="px-3 py-2">{row.requirement_title}</td>
                        <td className="px-3 py-2">
                          {row.version_label}
                          {row.superseded ? (
                            <Badge tone="neutral" className="ml-1.5">
                              Superseded
                            </Badge>
                          ) : null}
                        </td>
                        <td className="px-3 py-2">
                          <StatusChip value={row.decision} tones={STATUS_TONES} />
                        </td>
                        <td className="px-3 py-2 capitalize text-muted">{row.platform}</td>
                      </tr>
                      {expanded ? (
                        <tr className="bg-surface-2/50">
                          <td />
                          <td colSpan={6} className="px-3 py-3">
                            <RecordEvidence row={row} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </ScrollArea>
        )}
        <Pager
          page={page}
          pageSize={pageSize}
          total={records.data?.total ?? 0}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </Card>
    </div>
  );
}
